/* eslint-disable graphql/template-strings */
import { useApolloClient, useMutation, gql } from '@apollo/client';
import React from 'react';
import EventsSynchronizer from '~Components/EventsSynchronizer';
import { useStorage } from '~Components/Storage';

const CASH_SNAPSHOTS_QUERY = gql`
  query GetCashSnapshot($deviceId: ID!, $venueId: String!) {
    POSDevice(id: $deviceId) {
      cashEvents {
        snapshot(venueId: $venueId)
      }
    }
  }
`;

const CASH_EVENTS_QUERY = gql`
  query GetCashEvents($deviceId: ID!, $sentAfter: DateTime!) {
    POSDevice(id: $deviceId) {
      cashEvents {
        events(sentAfter: $sentAfter) {
          lastKey
          items
        }
      }
    }
  }
`;

const ADD_CASH_EVENT_MUTATION = gql`
  mutation AddCashEvents($deviceId: String!, $venueId: String!, $events: Json!) {
    addCashEvents(deviceId: $deviceId, venueId: $venueId, events: $events) {
      success
      message
    }
  }
`;

interface Props {
  venueId: string;
}

const CashSynchronizer: React.FunctionComponent<Props> = ({ venueId }) => {
  const client = useApolloClient();
  const [addCashEvents] = useMutation(ADD_CASH_EVENT_MUTATION);

  const [storage] = useStorage();

  if (!storage.taxComplianceDeviceId) {
    return null;
  }

  return (
    <EventsSynchronizer
      venueId={venueId}
      eventType="ADD_CASH_EVENT"
      snapshotType="SET_CASH_SNAPSHOT"
      eventSelector={(state) => state.cashEvents}
      snapshotSelector={(state) => state.cashSnapshot}
      // Poll every minute
      pollingInterval={60 * 1000}
      /* Polling query for new events */
      queryEvents={async (timestamp) => {
        const response = await client.query({
          query: CASH_EVENTS_QUERY,
          variables: {
            deviceId: storage.taxComplianceDeviceId,
            venueId,
            sentAfter: timestamp,
          },
          fetchPolicy: 'network-only',
        });

        const events = response.data?.POSDevice?.cashEvents?.events?.items;

        return {
          success: Boolean(events),
          events: events,
          error: (response.errors && response.errors[0].message) || null,
        };
      }}
      /* Snapshot query */
      querySnapshot={async () => {
        const response = await client.query({
          query: CASH_SNAPSHOTS_QUERY,
          variables: {
            deviceId: storage.taxComplianceDeviceId,
            venueId,
          },
          fetchPolicy: 'network-only',
        });

        const snapshot = response.data?.POSDevice?.cashEvents.snapshot;

        return {
          success: Boolean(snapshot),
          snapshot: snapshot || null,
          timestamp: snapshot?.date || null,
          error: (response.errors && response.errors[0].message) || null,
        };
      }}
      addEventMutation={async (events) => {
        const response = await addCashEvents({
          variables: {
            deviceId: storage.taxComplianceDeviceId,
            venueId,
            events,
          },
        });

        if (!response.data?.addCashEvents?.success) {
          return {
            success: false,
            error: response.errors?.map((error) => error.message).join(', ') || 'Unknown',
          };
        }

        return {
          error: null,
          success: true,
        };
      }}
    />
  );
};

export default CashSynchronizer;
